"use client";

import React, { useState } from "react";
import Icon from "@/components/ui/Icon";

interface BlogCodeBlockProps {
  code: string;
}

export default function BlogCodeBlock({ code }: BlogCodeBlockProps) {
  const [copied, setCopied] = useState(false);

  // Guess language from the first line comment or common tokens
  const detectLanguage = (str: string) => {
    const firstLine = str.trim().split("\n")[0] || "";
    if (firstLine.includes("#") || firstLine.startsWith("sudo") || firstLine.startsWith("npm") || firstLine.startsWith("docker")) return "BASH";
    if (firstLine.includes("import") || firstLine.includes("export") || firstLine.includes("interface") || firstLine.includes("const")) return "TYPESCRIPT";
    if (firstLine.includes("SELECT") || firstLine.includes("CREATE") || firstLine.includes("ALTER")) return "SQL";
    if (firstLine.includes("apiVersion") || firstLine.includes("kind:")) return "YAML";
    if (firstLine.startsWith("{") || firstLine.startsWith("[")) return "JSON";
    if (str.includes("+---") || str.includes("|===")) return "DIAGRAM";
    return "CODE";
  };

  const language = detectLanguage(code);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="relative group my-7 rounded-xl overflow-hidden border border-outline-variant/30 bg-[#161210] shadow-xl">
      {/* Code Header Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#1F1916] border-b border-white/5 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80 inline-block"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80 inline-block"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80 inline-block"></span>
          <span className="ml-2 font-mono font-bold text-[11px] tracking-wider text-neutral-400 uppercase">
            {language}
          </span>
        </div>

        <button
          onClick={handleCopy}
          type="button"
          aria-label={copied ? "Code copied to clipboard" : "Copy code to clipboard"}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-neutral-300 hover:text-white bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
        >
          {copied ? (
            <>
              <Icon name="check" size={14} className="text-emerald-400" />
              <span className="text-emerald-400">Copied!</span>
            </>
          ) : (
            <>
              <Icon name="file_text" size={14} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code Content */}
      <pre className="p-5 overflow-x-auto font-mono text-[0.85rem] md:text-[0.9rem] leading-relaxed text-emerald-300/90 select-all">
        <code>{code}</code>
      </pre>
    </div>
  );
}
